import { useState, useEffect } from "react";
import { Icon, Loader, Menu, MenuItem } from "semantic-ui-react";
import { ForumPost } from "../ForumPost";
import "./PostList.scss";

export function PostList(props) {
  const { postList, loading, pagination, currentPage, setCurrentPage, onReload } = props;
  const [posts, setPosts] = useState(postList || []);

  useEffect(() => {
    setPosts(postList || []);
  }, [postList]);

  const getTotalPages = () => {
    if (!pagination) return 1;
    return pagination.totalPages || 1;
  };

  const getPaginationInfo = () => {
    if (!pagination || !posts) return "No hay datos disponibles";

    const itemsPerPage = pagination.limit || 10;
    const totalItems = pagination.totalItems || pagination.total || 0;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return `Mostrando ${startItem}-${endItem} de ${totalItems} registros`;
  };

  const renderPageNumbers = () => {
    const totalPages = getTotalPages();
    const pages = [];
    const maxVisiblePages = 5;
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const maxPagesBeforeCurrentPage = Math.floor(maxVisiblePages / 2);
      const maxPagesAfterCurrentPage = Math.ceil(maxVisiblePages / 2) - 1;

      if (currentPage <= maxPagesBeforeCurrentPage) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
        startPage = totalPages - maxVisiblePages + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - maxPagesBeforeCurrentPage;
        endPage = currentPage + maxPagesAfterCurrentPage;
      }
    }

    // Agregar primera página si no está visible
    if (startPage > 1) {
      pages.push(
        <MenuItem as="a" key={1} onClick={() => setCurrentPage(1)}>
          1
        </MenuItem>
      );

      if (startPage > 2) {
        pages.push(
          <MenuItem as="a" key="ellipsis-start" className="ellipsis" disabled>
            ...
          </MenuItem>
        );
      }
    }

    // Páginas visibles
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <MenuItem
          as="a"
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </MenuItem>
      );
    }

    // Agregar última página si no está visible
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <MenuItem as="a" key="ellipsis-end" className="ellipsis" disabled>
            ...
          </MenuItem>
        );
      }

      pages.push(
        <MenuItem
          as="a"
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </MenuItem>
      );
    }

    return pages;
  };

  const handlePageChange = (_, { activePage }) => {
    setCurrentPage(activePage);
  };

  return (
    <div>
      {loading ? (
        <div className="post-list__loader">
          <Loader active inline="centered" size="large">
            Cargando publicaciones...
          </Loader>
        </div>
      ) : (
        <>
          <div className="post-list">
            {posts.map((post) => (
              <ForumPost key={post.id} post={post} />
            ))}
          </div>
          {posts.length === 0 && (
            <div className="post-list__empty-state">
              <div className="post-list__empty-state-icon">
                <Icon name="comments outline" />
              </div>
              <h3 className="post-list__empty-state-title">
                No hay publicaciones disponibles
              </h3>
              <p className="post-list__empty-state-description">
                Aún no hay publicaciones en el foro que coincidan con los filtros seleccionados.
                <br />
                ¡Sé el primero en compartir conocimiento con la comunidad estudiantil!
              </p>
            </div>
          )}
          {pagination && posts.length > 0 && (
            <div className="post-list__pagination">
              <div className="post-list__pagination-info">
                <span>{getPaginationInfo()}</span>
              </div>

              <Menu floated="right" pagination>
                <MenuItem
                  as="a"
                  icon
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  title="Primera página"
                >
                  <Icon name="angle double left" />
                </MenuItem>
                <MenuItem
                  as="a"
                  icon
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  title="Página anterior"
                >
                  <Icon name="angle left" />
                </MenuItem>
                {renderPageNumbers()}
                <MenuItem
                  as="a"
                  icon
                  disabled={currentPage === getTotalPages()}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  title="Página siguiente"
                >
                  <Icon name="angle right" />
                </MenuItem>
                <MenuItem
                  as="a"
                  icon
                  disabled={currentPage === getTotalPages()}
                  onClick={() => setCurrentPage(getTotalPages())}
                  title="Última página"
                >
                  <Icon name="angle double right" />
                </MenuItem>
              </Menu>
            </div>
          )}
        </>
      )}
    </div>
  );
}
